import { ref, useApm, useCoreLogger, toError, type ApiError, type OpenapiError } from '#imports'
import type { ApiResponse, UseMutationMutateOptions } from '../../types'
import { isServerError } from '../../shared/api/errors'
import { consumeMutationErrorCapture, runWithMutationErrorCapture } from '../../shared/api/mutationErrorCapture'

function captureMutationError(apm: ReturnType<typeof useApm>, error: unknown, errorClass: UseMutationMutateOptions['errorClass'], errorLabels?: Record<string, string>) {
    // This capture supersedes the HTTP layer's deferred one for the same error.
    consumeMutationErrorCapture(error)

    const captured = toError(error)

    let resolvedClass: 'expected' | undefined
    try {
        resolvedClass = typeof errorClass === 'function' ? errorClass(error) : errorClass
    }
    catch {
        // A throwing predicate must never break error reporting — capture unlabelled.
        resolvedClass = undefined
    }

    const labels: Record<string, string> = { ...errorLabels }

    // A 5xx is a real server failure, never "expected" — it keeps its diagnostic labels
    // but stays unclassified so it still surfaces on error dashboards.
    if (resolvedClass === 'expected' && !isServerError(error)) {
        labels.error_class = 'expected'
    }

    if (Object.keys(labels).length) {
        apm.captureError(captured, { labels })
        return
    }

    apm.captureError(captured)
}

function resolveErrorLabels<TBody>(errorLabels: ((error: unknown, body?: TBody) => Record<string, string> | undefined) | undefined, error: unknown, body: TBody) {
    try {
        return errorLabels?.(error, body)
    }
    catch {
        // A throwing resolver must never break error reporting — omit its labels.
        return undefined
    }
}

export function useMutation<TResponse = ApiResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null, TBody = any>(handler: (body: TBody) => Promise<TResponse>, options?: UseMutationMutateOptions<TResponse, TError, TBody>) {
    const apm = useApm()
    const logger = useCoreLogger()

    // Data
    const body = ref<TBody | undefined>()
    const data = ref<TResponse | undefined>()
    const error = ref<TError | undefined>()

    // States
    const pending = ref<boolean>(false)
    const isError = ref<boolean>(false)
    const isSuccess = ref<boolean>(false)

    function reset() {
        body.value = undefined
        data.value = undefined
        error.value = undefined
        isError.value = false
        isSuccess.value = false
    }

    function _shouldRun(mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        const mode = mutateOptions?.mode || options?.mode || 'parallel'

        if (mode === 'leading') return !pending.value

        // Default: parallel
        return true
    }

    async function _run(_body: TBody, mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        reset()
        pending.value = true

        body.value = _body

        let response: TResponse | undefined
        let responseError: unknown

        try {
            // The scope covers only the handler and its pre-hooks; onSuccess/onError/onSettled
            // run after it closes, so deferred captures flush before a callback can navigate away.
            const result = await runWithMutationErrorCapture(async () => {
                if (options?.onMutate) {
                    await options.onMutate(_body)
                }

                if (mutateOptions?.onMutate) {
                    await mutateOptions.onMutate(_body)
                }

                return await handler(_body) as TResponse
            })

            data.value = result as typeof data.value
            isSuccess.value = true

            if (options?.onSuccess) {
                await options.onSuccess(result, _body)
            }

            if (mutateOptions?.onSuccess) {
                await mutateOptions.onSuccess(result, _body)
            }

            response = result
        }
        catch (_error) {
            error.value = _error as typeof error.value
            isError.value = true

            logger.error(_error)

            captureMutationError(apm, _error, mutateOptions?.errorClass ?? options?.errorClass, resolveErrorLabels(mutateOptions?.errorLabels ?? options?.errorLabels, _error, _body))

            if (options?.onError) {
                await options.onError(error.value as TError, _body)
            }

            if (mutateOptions?.onError) {
                await mutateOptions.onError(error.value as TError, _body)
            }

            responseError = _error
        }
        finally {
            if (options?.onSettled) {
                await options.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            if (mutateOptions?.onSettled) {
                await mutateOptions.onSettled(data.value as TResponse, error.value as TError, _body)
            }

            pending.value = false
        }

        return { response, responseError }
    }

    async function mutate(_body: TBody, mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>) {
        if (!_shouldRun(mutateOptions)) return

        await _run(_body, mutateOptions)
    }

    async function mutateAsync(_body: TBody, mutateOptions?: UseMutationMutateOptions<TResponse, TError, TBody>): Promise<TResponse | undefined> {
        if (!_shouldRun(mutateOptions)) return

        const { response, responseError } = await _run(_body, mutateOptions)

        if (responseError) throw responseError

        return response
    }

    return {
        body,
        data,
        isSuccess,
        error,
        isError,
        pending,
        mutate,
        mutateAsync,
        reset,
    }
}
